import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import React, { useState } from 'react'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import {
  formatStatementAmount,
  formatStatementDateShort,
  formatStatementPeriod,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatements.format'
import type { StatementSummary } from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsDemoData'
import StatementDetailModal from 'src/page-components/Accounts/AccountStatementsTab/components/StatementDetailModal/StatementDetailModal'
import * as style from 'src/page-components/Accounts/AccountStatementsTab/components/StatementListRow/StatementListRow.style'

interface StatementListRowProps {
  statement: StatementSummary
  /** Card label passed through to the detail modal's description. */
  cardLabel: string
  /**
   * Set on the first row revealed by "See more statements" so the list can move
   * focus here — the control the user activated unmounts itself.
   */
  viewButtonRef?: React.Ref<HTMLButtonElement>
}

/**
 * One statement row in the card-detail Statements list (EX-40600).
 *
 * Current and paid statements share one anatomy — period, owed/due-or-paid
 * detail, status, amount, View — and only the status and the detail copy change.
 * Keeping them identical is a design guardrail, so resist splitting this into
 * two row components.
 *
 * The row owns its own detail-open state; nothing above it tracks which
 * statement is open.
 *
 * Deviation from the design prototype, deliberate: the prototype also makes the
 * whole row clickable as a mouse convenience via a `<div onClick>`. That is
 * banned by `src/CLAUDE.md` ("No `<div onClick>`"), and the prototype itself
 * treats View as the real accessible trigger, so View is the only trigger here.
 * No keyboard or pointer affordance is lost.
 *
 * Download is intentionally absent: it is Phase 2 and out of the Phase-1 build.
 */
const StatementListRow: React.FC<StatementListRowProps> = ({
  statement,
  cardLabel,
  viewButtonRef,
}) => {
  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()
  const [isDetailOpen, setIsDetailOpen] = useState(false)

  const period = formatStatementPeriod(statement.period)
  const isPaid = statement.status === 'PAID'
  const owed = formatStatementAmount(formatCurrency, statement.amountOwed)

  const detail = isPaid
    ? translate('Owed ${owed} · Paid ${paid}', {
        owed,
        paid: formatStatementAmount(
          formatCurrency,
          statement.amountPaid ?? statement.amountOwed,
        ),
      })
    : translate('Owed ${owed} · Payment due ${dueOn}', {
        owed,
        dueOn: formatStatementDateShort(statement.dueOn),
      })

  return (
    <li css={style.row} data-testid="statementListRow">
      <div css={style.rowContent}>
        <span css={style.period}>
          <span css={style.periodTitle}>{period}</span>
          <span css={style.periodDetail}>{detail}</span>
        </span>
        <span css={style.rowActions}>
          {/* Status text is visible, never colour-only. */}
          <Pill color={isPaid ? 'saas' : 'pending'} size="small">
            {isPaid ? translate('Paid') : translate('Open')}
          </Pill>
          <span css={style.amount}>
            {formatStatementAmount(formatCurrency, statement.statementAmount)}
          </span>
          <Button
            ref={viewButtonRef}
            variant="text"
            aria-haspopup="dialog"
            // Each row's control needs a distinct accessible name — a list of
            // identical "View" buttons is unusable from a screen reader's
            // element list.
            aria-label={translate('View ${period} statement', { period })}
            onClick={() => setIsDetailOpen(true)}
          >
            {translate('View')}
          </Button>
        </span>
      </div>
      {/*
        Mounted only while open. `open` is a prop, not a render guard, so
        leaving it always-mounted made every row build its full ledger — ~24
        currency formats each, plus a permanent react-modal portal node — for
        eight statements that are almost always closed. react-modal returns
        focus to the trigger on unmount, so View still regains focus on close.
      */}
      {isDetailOpen && (
        <StatementDetailModal
          open
          onClose={() => setIsDetailOpen(false)}
          statement={statement}
          cardLabel={cardLabel}
        />
      )}
    </li>
  )
}

export default StatementListRow
